/**
 * Your savings against courses taken plays once when it comes on screen: the line draws from the
 * first course to the fortieth, a point pops in at every fifth course as the line reaches it, the
 * dark badge pops beside the first point, and the rider (the point at the line's end with the brand
 * badge) travels the line as it draws, its badge popping in and counting up to $40,000 like an
 * odometer. Every cue is the composition's (101 frames at 25 fps), in milliseconds on the elements'
 * data attributes.
 *
 * The line is a stroke drawn through a dash offset, the one main-thread property here, and its
 * animation exists only for the draw. The points, the badges, the rider, and the digit columns move
 * by translate, scale, and opacity on the compositor. The pending state holds every part hidden until
 * the piece plays (modules/motion/once.js); the resting markup is the finished chart.
 */
import {EASING_CURVES} from '../../modules/motion/easing.js';
import {rollDigit} from '../../modules/motion/digit.js';
import {cue, playOnce} from '../../modules/motion/once.js';
import {drawStroke} from '../../modules/motion/stroke.js';

const SELECTOR = Object.freeze({
    root: '.js-motionYouSave',
    line: '.js-motionYouSaveLine',
    point: '.js-motionYouSavePoint',
    badge: '.js-motionYouSaveBadge',
    rider: '.js-motionYouSaveRider',
    text: '.js-motionYouSaveText',
    column: '.js-motionYouSaveColumn',
});

/** Milliseconds. The line's cue; every other part carries its own. */
const LINE = Object.freeze({delay: 200, duration: 2800});

const EASE = Object.freeze({
    /* The original savings composition gives the line and its rider separate curves. */
    draw: 'cubic-bezier(0.456, 0, 0.08, 1)',
    ride: 'cubic-bezier(0.486, 0, 0.042, 1)',
    glide: EASING_CURVES['ease-in-out-quad'],
    fade: EASING_CURVES['ease-out'],
    /* A point grows past its size and settles, as the composition pops it. */
    pop: EASING_CURVES['ease-out-back'],
});

/** Hold the first keyframe through the delay; the natural styles take over at the end. */
const FILL = 'backwards';

/**
 * @param {HTMLElement} root
 * @param {import('../../modules/motion/run.js').Run} run
 */
function start(root, run) {
    const line = root.querySelector(SELECTOR.line);
    if (line instanceof SVGElement) {
        run.add(drawStroke(line, {...LINE, easing: EASE.draw}));
    }
    for (const point of root.querySelectorAll(SELECTOR.point)) {
        if (point instanceof HTMLElement) {
            run.add(point.animate([{scale: 0}, {scale: 1}], {...cue(point), easing: EASE.pop, fill: FILL}));
        }
    }
    for (const badge of root.querySelectorAll(SELECTOR.badge)) {
        if (badge instanceof HTMLElement) {
            run.add(badge.animate([{scale: 0}, {scale: 1}], {...cue(badge), easing: EASE.fade, fill: FILL}));
        }
    }
    const rider = root.querySelector(SELECTOR.rider);
    if (rider instanceof HTMLElement) {
        run.add(rider.animate([{translate: rider.dataset.from}, {translate: '0 0'}], {...cue(rider), easing: EASE.ride, fill: FILL}));
    }
    const text = root.querySelector(SELECTOR.text);
    if (text instanceof HTMLElement) {
        run.add(text.animate([{opacity: 0}, {opacity: 1}], {...cue(text), easing: EASE.fade, fill: FILL}));
    }
    for (const column of root.querySelectorAll(SELECTOR.column)) {
        if (column instanceof HTMLElement) {
            run.add(rollDigit(column, {...cue(column), easing: EASE.glide}));
        }
    }
}

/**
 * Initialize every chart that is not initialized yet. Safe to call after content swaps.
 */
export function initializeComponent() {
    for (const root of document.querySelectorAll(SELECTOR.root)) {
        if (root instanceof HTMLElement) {
            playOnce(root, (run) => start(root, run));
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponent);
} else {
    initializeComponent();
}
document.addEventListener('htmx:load', initializeComponent);
